import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { forgetNativePushToken } from "@/lib/nativePush";
import { writeLocalSettings } from "@/lib/settings";
import { localCharacter } from "@/lib/avatarConfig";
import { toast } from "sonner";
import { api, getAuthToken, setAuthToken, isTokenRemembered, SESSION_ENDED_EVENT, SUPERSEDED_MESSAGE } from "@/lib/api";

// How often a signed-in device checks it is still the account's current
// session, so an idle tab notices a sign-in elsewhere without a reload.
const SESSION_CHECK_MS = 15000;
// Start-up retries while the server is unreachable: each try gives up after
// BOOT_REQUEST_MS (a hung server never answers, which left the page blank
// forever), and the whole wait stops after BOOT_DEADLINE_MS.
const BOOT_RETRY_MS = 2000;
const BOOT_REQUEST_MS = 8000;
const BOOT_DEADLINE_MS = 30000;
const SERVER_DOWN_MESSAGE = "Can't reach the Jangle server right now. We'll keep trying.";
// Survives the redirect to /login so the login page can explain why.
export const SIGNED_OUT_NOTICE_KEY = "jangle.auth.signedOutNotice";

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  playAsGuest: async () => {},
  upgradeGuest: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Only a 401 means signed out. A network error or a server error (the
  // backend restarting during a deploy, a phone waking with no signal) keeps
  // the current user, and returns undefined so the caller can try again.
  const refreshUser = useCallback(async (opts) => {
    try {
      const { data } = await api.get("/auth/me", opts);
      setUser(data);
      return data;
    } catch (e) {
      if (e?.response?.status === 401 || !getAuthToken()) {
        setUser(null);
        return null;
      }
      return undefined;
    }
  }, []);

  useEffect(() => {
    (async () => {
      // With a saved token, keep the loading screen up and retry while the
      // server can't be reached, rather than showing the sign-in page.
      const deadline = Date.now() + BOOT_DEADLINE_MS;
      const boot = { timeout: BOOT_REQUEST_MS };
      let me = await refreshUser(boot);
      while (me === undefined && Date.now() + BOOT_RETRY_MS < deadline) {
        await new Promise((r) => setTimeout(r, BOOT_RETRY_MS));
        me = await refreshUser(boot);
      }
      setLoading(false);
      if (me === undefined) toast.error(SERVER_DOWN_MESSAGE, { id: "server-down", duration: 15000 });
      // Remembered sign-in: swap the token for a fresh one on each visit so
      // a regular player is never signed out by expiry.
      // A raider picked before the server stored raiders lives only in this
      // browser, so opponents saw a different one. Save it to the account.
      const local = me && !me.avatar3d && localCharacter(me.id);
      if (local) {
        api.put("/me/avatar3d", { character: local }).then(({ data }) => setUser(data)).catch(() => {});
      }
      if (me && isTokenRemembered()) {
        api.post("/auth/renew")
          .then(({ data }) => { if (data?.access_token) setAuthToken(data.access_token, { remember: true }); })
          .catch(() => {});
      }
    })();
  }, [refreshUser]);

  // Pages render nothing until we know who is signed in, so the loading
  // screen in public/index.html stays up until then.
  useEffect(() => {
    if (!loading) requestAnimationFrame(() => window.__bootDone?.());
  }, [loading]);

  // Another device signed in to this account: sign this one out now.
  useEffect(() => {
    const onEnded = () => {
      setAuthToken(null);
      setUser((prev) => {
        if (prev) {
          try { sessionStorage.setItem(SIGNED_OUT_NOTICE_KEY, SUPERSEDED_MESSAGE); } catch { /* ignore */ }
          toast.error(SUPERSEDED_MESSAGE, { id: "session-superseded" });
        }
        return null;
      });
    };
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
  }, []);

  const signedIn = !!user;

  // Keep the game page's copy of the account's settings up to date.
  const settingsJson = JSON.stringify(user?.settings || null);
  useEffect(() => {
    if (user?.settings) writeLocalSettings(user.settings);
  }, [settingsJson]); // eslint-disable-line react-hooks/exhaustive-deps

  // Still holding a token but the server couldn't be reached at start-up:
  // keep trying, and sign straight back in once it answers.
  useEffect(() => {
    if (loading || signedIn || !getAuthToken()) return undefined;
    const retry = () => { if (getAuthToken()) refreshUser(); };
    const id = setInterval(retry, SESSION_CHECK_MS);
    window.addEventListener("online", retry);
    window.addEventListener("focus", retry);
    return () => {
      clearInterval(id);
      window.removeEventListener("online", retry);
      window.removeEventListener("focus", retry);
    };
  }, [loading, signedIn, refreshUser]);

  // While signed in, check the session is still current: on an interval and
  // whenever the tab comes back to the foreground. A superseded session gets
  // a 401 that the api interceptor turns into SESSION_ENDED_EVENT.
  useEffect(() => {
    if (!signedIn) return undefined;
    const check = () => { api.get("/auth/me").catch(() => {}); };
    const id = setInterval(check, SESSION_CHECK_MS);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, [signedIn]);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    // Persist the bearer token so subsequent requests authenticate even
    // when third-party cookies are blocked (incognito, Safari ITP, etc).
    if (data?.access_token) setAuthToken(data.access_token, { remember });
    setUser(data.user);
    return data.user;
  };

  const register = async (email, username, password) => {
    const { data } = await api.post("/auth/register", { email, username, password });
    if (data?.access_token) setAuthToken(data.access_token, { remember: true });
    setUser(data.user);
    return data.user;
  };

  // Play as guest: a temporary account on this device (backend guests.py).
  const playAsGuest = async () => {
    const { data } = await api.post("/auth/guest");
    if (data?.access_token) setAuthToken(data.access_token, { remember: true });
    setUser(data.user);
    return data.user;
  };

  // A guest creating a full account; the same account, so nothing is lost.
  const upgradeGuest = async (email, username, password) => {
    const { data } = await api.post("/auth/upgrade", { email, username, password });
    if (data?.access_token) setAuthToken(data.access_token, { remember: true });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await forgetNativePushToken();
      await api.post("/auth/logout");
    } finally {
      setAuthToken(null);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, playAsGuest, upgradeGuest, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
